import api from './api';

export interface MetadataStep {
  id: string;
  stepId: string;
  title: string;
  description: string;
  element: string;
  position: 'top' | 'bottom' | 'left' | 'right' | 'center' | 'bottom-center';
  tooltipVariant: 'Info' | 'Media';
  modal?: 'popover';
  mediaType?: 'video' | 'gif';
  mediaUrl?: string;
  mediaAlt?: string;
  backButtonText?: string;
  nextButtonText?: string;
  sortOrder: number;
  isActive: boolean;
}

export interface OnboardingTourSnapshot {
  currentStep: MetadataStep | null;
  currentStepIndex: number;
  totalSteps: number;
  isActive: boolean;
  showCloseButton: boolean;
  currentFeature: string | null;
  isRestart: boolean;
}

interface RawMetadataStep {
  id?: unknown;
  stepId?: unknown;
  title?: unknown;
  description?: unknown;
  element?: unknown;
  position?: unknown;
  tooltipVariant?: unknown;
  modal?: unknown;
  mediaType?: unknown;
  mediaUrl?: unknown;
  MediaUrl?: unknown;
  mediaAlt?: unknown;
  backButtonText?: unknown;
  nextButtonText?: unknown;
  sortOrder?: unknown;
  isActive?: unknown;
}

interface RawFeature {
  featureKey?: unknown;
  steps?: unknown;
}

interface RawRegistryDocument {
  Properties?: {
    Feature?: unknown;
    Version?: unknown;
  };
}

type Listener = () => void;

const DEFAULT_LANGUAGE = 'en-US';
const NEW_RECENT_TABS_FEATURE_KEY = 'new-recent-tabs';
const NEW_RECENT_TABS_SELECTOR = '[data-tour-id="new-recent-tabs"]';
const ACTIVITY_FEATURE_KEY = 'activity';
const ACTIVITY_NEW_AGENT_SELECTOR = '[data-tour-id="activity-new-agent-btn"]';
const ACTIVITY_CREATE_TASK_SELECTOR = '[data-tour-id="activity-create-task-btn"]';
const WORKSPACE_LEFT_MENU_SELECTOR = '[data-tour-id="left-wizard-workspace-icon"]';

class OnboardingTourService {
  private readonly listeners = new Set<Listener>();
  private readonly featureTourRegistry = new Map<string, MetadataStep[]>();

  private currentStep: MetadataStep | null = null;
  private currentStepIndex = 0;
  private totalSteps = 0;
  private isActive = false;
  private showCloseButton = false;
  private currentFeature: string | null = null;
  private isRestart = false;

  private steps: MetadataStep[] = [];
  private allLoadedFeatures: RawFeature[] = [];
  private pendingFeatureKeys: string[] = [];
  private featureVersion: string | null = null;
  // Monotonically-increasing counter.  Each startFeatureTour call is stamped
  // with the counter value at invocation time.  If a newer startFeatureTour
  // or stopTour call arrives while validateUser is still in-flight, the older
  // call detects that its stamp is stale and discards its result — preventing
  // a later-resolving profile-header validation from overwriting a
  // new-recent-tabs tour that has already started.
  private tourStartGeneration = 0;

  private snapshot: OnboardingTourSnapshot = {
    currentStep: null,
    currentStepIndex: 0,
    totalSteps: 0,
    isActive: false,
    showCloseButton: false,
    currentFeature: null,
    isRestart: false,
  };

  subscribe = (listener: Listener): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): OnboardingTourSnapshot => this.snapshot;

  private refreshSnapshot(): void {
    this.snapshot = {
      currentStep: this.currentStep,
      currentStepIndex: this.currentStepIndex,
      totalSteps: this.totalSteps,
      isActive: this.isActive,
      showCloseButton: this.showCloseButton,
      currentFeature: this.currentFeature,
      isRestart: this.isRestart,
    };
  }

  private emit(): void {
    this.listeners.forEach((listener) => listener());
  }

  private setPartial(snapshot: Partial<OnboardingTourSnapshot>): void {
    if (snapshot.currentStep !== undefined) this.currentStep = snapshot.currentStep;
    if (snapshot.currentStepIndex !== undefined) this.currentStepIndex = snapshot.currentStepIndex;
    if (snapshot.totalSteps !== undefined) this.totalSteps = snapshot.totalSteps;
    if (snapshot.isActive !== undefined) this.isActive = snapshot.isActive;
    if (snapshot.showCloseButton !== undefined) this.showCloseButton = snapshot.showCloseButton;
    if (snapshot.currentFeature !== undefined) this.currentFeature = snapshot.currentFeature;
    if (snapshot.isRestart !== undefined) this.isRestart = snapshot.isRestart;
    this.refreshSnapshot();
    this.emit();
  }

  registerFeatureTour(featureKey: string, steps: MetadataStep[]): void {
    if (!featureKey || !steps.length) {
      return;
    }
    this.featureTourRegistry.set(featureKey, steps);
  }

  getRegisteredTourSteps(featureKey: string): MetadataStep[] | null {
    return this.featureTourRegistry.get(featureKey) ?? null;
  }

  private normalizeMediaUrl(rawUrl: unknown): string | undefined {
    if (typeof rawUrl !== 'string') {
      return undefined;
    }

    const url = rawUrl.trim();
    if (!url) {
      return undefined;
    }

    if (/^(https?:|blob:|data:)/i.test(url)) {
      return url;
    }

    if (url.startsWith('/')) {
      return url;
    }

    const withoutDotSlash = url.startsWith('./') ? url.slice(2) : url;
    if (withoutDotSlash.toLowerCase().startsWith('assets/')) {
      return `/${withoutDotSlash}`;
    }

    const assetsIndex = withoutDotSlash.toLowerCase().indexOf('/assets/');
    if (assetsIndex >= 0) {
      return withoutDotSlash.slice(assetsIndex);
    }

    return withoutDotSlash;
  }

  private asString(value: unknown): string {
    return typeof value === 'string' ? value : '';
  }

  private normalizeSelector(value: string): string {
    const token = value.trim();
    if (!token) {
      return '';
    }

    if (
      token.startsWith('[')
      || token.startsWith('.')
      || token.startsWith('#')
      || token.includes(' ')
      || token.includes('>')
      || token.includes(':')
    ) {
      return token;
    }

    if (/^[a-zA-Z0-9_-]+$/.test(token)) {
      return `[data-tour-id="${token}"]`;
    }

    return token;
  }

  private applyStepOverrides(step: MetadataStep): MetadataStep {
    const id = step.id.toLowerCase();
    const stepId = step.stepId.toLowerCase();
    const element = step.element.toLowerCase();
    const allTokens = [id, stepId, element].filter((value) => !!value);

    if (allTokens.some((value) =>
      value.includes('activity-new-agent-btn')
      || value.includes('new-agent')
      || value === 'newagent'
    )) {
      return {
        ...step,
        element: ACTIVITY_NEW_AGENT_SELECTOR,
      };
    }

    if (allTokens.some((value) =>
      value.includes('activity-create-task-btn')
      || value.includes('activity-create-task')
    )) {
      return {
        ...step,
        element: ACTIVITY_CREATE_TASK_SELECTOR,
      };
    }

    if (
      stepId === NEW_RECENT_TABS_FEATURE_KEY ||
      id.includes('newrecenttabs') ||
      element.includes('new-recent-tabs')
    ) {
      return {
        ...step,
        element: NEW_RECENT_TABS_SELECTOR,
        tooltipVariant: 'Info',
        modal: undefined,
        mediaType: undefined,
        mediaUrl: undefined,
        mediaAlt: undefined,
      };
    }

    return step;
  }

  private mapRawStepToMetadataStep(step: RawMetadataStep): MetadataStep {
    const rawMediaUrl = step?.mediaUrl ?? step?.MediaUrl;

    return {
      id: String(step?.id ?? ''),
      stepId: this.asString(step?.stepId),
      title: this.asString(step?.title),
      description: this.asString(step?.description),
      element: this.normalizeSelector(this.asString(step?.element)),
      position: this.toPosition(step?.position),
      modal: step?.modal === 'popover' ? 'popover' : undefined,
      tooltipVariant: step?.tooltipVariant === 'Media' ? 'Media' : 'Info',
      mediaType: step?.mediaType === 'video' || step?.mediaType === 'gif' ? step.mediaType : undefined,
      mediaUrl: this.normalizeMediaUrl(rawMediaUrl),
      mediaAlt: typeof step?.mediaAlt === 'string' ? step.mediaAlt : undefined,
      backButtonText: typeof step?.backButtonText === 'string' ? step.backButtonText : undefined,
      nextButtonText: typeof step?.nextButtonText === 'string' ? step.nextButtonText : undefined,
      sortOrder: typeof step?.sortOrder === 'number' ? step.sortOrder : 0,
      isActive: step?.isActive === true,
    };
  }

  private isElementVisible(selector: string): boolean {
    const element = document.querySelector(selector);
    if (!(element instanceof HTMLElement)) {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    const rect = element.getBoundingClientRect();
    if (rect.width <= 0 || rect.height <= 0) {
      return false;
    }

    const inViewport = rect.bottom > 0
      && rect.right > 0
      && rect.top < window.innerHeight
      && rect.left < window.innerWidth;

    return inViewport;
  }

  private waitForActivityDashboardReady(timeoutMs = 12000): Promise<void> {
    const startedAt = Date.now();

    return new Promise((resolve) => {
      const check = () => {
        const hasDashboardRoot = !!document.querySelector('.dashboard-wrapper, .dashboard-container');
        const hasCreateTaskTarget = this.isElementVisible(ACTIVITY_CREATE_TASK_SELECTOR);
        const hasNewAgentTarget = this.isElementVisible(ACTIVITY_NEW_AGENT_SELECTOR);

        // Consider dashboard ready once its container is mounted and at least
        // one activity tour anchor is available in the DOM.
        if (hasDashboardRoot && (hasCreateTaskTarget || hasNewAgentTarget)) {
          resolve();
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          // Avoid blocking the tour forever if the page structure changes.
          resolve();
          return;
        }

        window.setTimeout(check, 120);
      };

      check();
    });
  }

  private toPosition(position: unknown): MetadataStep['position'] {
    switch (position) {
      case 'top':
      case 'bottom':
      case 'left':
      case 'right':
      case 'center':
      case 'bottom-center':
        return position;
      default:
        return 'center';
    }
  }

  private getMetadataContext(language: string): Array<{ key: string; value: string } | { Language: string }> {
    const screenType = (sessionStorage.getItem('SCREEN.TYPE') ?? 'Standalone').trim() || 'Standalone';
    const country = (sessionStorage.getItem('ctry') ?? 'US').trim() || 'US';

    return [
      { Language: language },
      { key: 'ctry', value: country },
      { key: 'SCREEN.TYPE', value: screenType },
    ];
  }

  private filterStepsForRenderedTargets(steps: MetadataStep[]): MetadataStep[] {
    if (typeof document === 'undefined' || document.querySelector(WORKSPACE_LEFT_MENU_SELECTOR)) {
      return steps;
    }

    return steps.filter((step) => step.element.trim() !== WORKSPACE_LEFT_MENU_SELECTOR);
  }

  async loadRegistryTours(documentKey = 'TourGuide', language = DEFAULT_LANGUAGE): Promise<void> {
    const endpoint = `/MetaData/Key/${encodeURIComponent(documentKey)}/${encodeURIComponent(language)}`;

    try {
      const response = await api.fetch<unknown>(endpoint, {
        method: 'POST',
        body: JSON.stringify(this.getMetadataContext(language)),
        timeoutMs: 30_000,
      });

      const doc = (Array.isArray(response)
        ? response[0]
        : this.getNestedData(response)) as RawRegistryDocument | undefined;

      this.featureVersion = typeof doc?.Properties?.Version === 'string'
        ? doc.Properties.Version
        : null;

      const features = doc?.Properties?.Feature;
      this.allLoadedFeatures = Array.isArray(features) ? features as RawFeature[] : [];
      this.featureTourRegistry.clear();

      for (const feature of this.allLoadedFeatures) {
        const featureKey = typeof feature?.featureKey === 'string' ? feature.featureKey : '';
        const rawSteps = Array.isArray(feature?.steps) ? feature.steps as RawMetadataStep[] : [];
        const steps = rawSteps
          .map((step) => this.mapRawStepToMetadataStep(step))
          .map((step) => this.applyStepOverrides(step))
          .filter((step: MetadataStep) => step.isActive);

        if (featureKey && steps.length) {
          this.registerFeatureTour(featureKey, steps);
        }
      }

      if (!this.featureTourRegistry.has(NEW_RECENT_TABS_FEATURE_KEY)) {
        this.registerFeatureTour(NEW_RECENT_TABS_FEATURE_KEY, [
          {
            id: 'newandrecenttabs-1',
            stepId: 'newandrecenttabs',
            title: 'New And Recent Tabs',
            description: 'Use this New Chat area to create a new tab and quickly access Recent Tabs.',
            element: NEW_RECENT_TABS_SELECTOR,
            position: 'center',
            tooltipVariant: 'Info',
            sortOrder: 1,
            isActive: true,
          },
        ]);
      }
    } catch (error) {
      console.warn('[OnboardingTourService] Registry tour metadata could not be loaded; continuing with local tours only.', error);
    }
  }

  private getNestedData(response: unknown): unknown {
    if (!response || typeof response !== 'object') {
      return response;
    }

    const responseRecord = response as { data?: unknown };
    if (Array.isArray(responseRecord.data)) {
      return responseRecord.data[0];
    }

    return responseRecord.data ?? response;
  }

  private async validateUser(featureKey: string): Promise<boolean> {
    if (!this.featureVersion) return false;
    try {
      const screenType = (sessionStorage.getItem('SCREEN.TYPE') ?? 'Standalone').trim() || 'Standalone';
      const result = await api.fetch<{ IsCompleted?: boolean; data?: { IsCompleted?: boolean } }>(
        '/ActivityEvent/ValidateUser',
        {
          method: 'POST',
          body: JSON.stringify({
            context: this.getMetadataContext(DEFAULT_LANGUAGE),
            DocType: 'TourGuide',
            FeatureKey: featureKey,
            FeatureVersion: this.featureVersion,
            ScreenType: screenType,
          }),
        },
      );
      return result?.IsCompleted === true || result?.data?.IsCompleted === true;
    } catch {
      // If the validation call fails, allow the tour to proceed.
      return false;
    }
  }

  async startFeatureTour(featureKey: string): Promise<void> {
    if (!featureKey) {
      return;
    }

    // Stamp this invocation with the current generation so that a concurrent
    // validateUser call for an earlier startFeatureTour (e.g. profile-header
    // that started when settings opened) cannot overwrite a tour that was
    // started more recently (e.g. new-recent-tabs triggered by + menu open).
    const generation = ++this.tourStartGeneration;

    // Skip the completion check when running in restart / re-watch mode so
    // the user can always replay a tour they have already finished.
    if (!this.isRestart) {
      const isCompleted = await this.validateUser(featureKey);

      // A newer startFeatureTour (or stopTour) arrived while validateUser was
      // pending — discard the stale result to prevent the race condition.
      if (generation !== this.tourStartGeneration) {
        return;
      }

      if (isCompleted) {
        this.stopTour(false);
        return;
      }
    }

    const stepsToLoad = this.filterStepsForRenderedTargets(
      this.getRegisteredTourSteps(featureKey) ?? [],
    );
    if (!stepsToLoad.length) {
      this.stopTour(false);
      return;
    }

    if (featureKey === ACTIVITY_FEATURE_KEY) {
      await this.waitForActivityDashboardReady();
    }

    this.steps = stepsToLoad;
    this.setPartial({
      currentFeature: featureKey,
      currentStepIndex: 0,
      currentStep: this.steps[0],
      totalSteps: this.steps.length,
      isActive: true,
    });
  }

  async startAllFeatureTours(featureKeys?: string[]): Promise<void> {
    this.isRestart = true;
    this.pendingFeatureKeys = featureKeys?.length
      ? [...featureKeys]
      : Array.from(this.featureTourRegistry.keys());

    this.showCloseButton = true;
    this.emit();
    await this.startNextQueuedTour();
  }

  private async startNextQueuedTour(): Promise<void> {
    if (!this.pendingFeatureKeys.length) {
      this.pendingFeatureKeys = [];
      return;
    }

    const nextFeatureKey = this.pendingFeatureKeys[0];
    const stepsToLoad = this.filterStepsForRenderedTargets(
      this.getRegisteredTourSteps(nextFeatureKey) ?? [],
    );

    if (!stepsToLoad.length) {
      this.pendingFeatureKeys.shift();
      await this.startNextQueuedTour();
      return;
    }

    if (nextFeatureKey === ACTIVITY_FEATURE_KEY) {
      await this.waitForActivityDashboardReady();
    }

    this.steps = stepsToLoad;
    this.setPartial({
      currentFeature: nextFeatureKey,
      currentStepIndex: 0,
      currentStep: this.steps[0],
      totalSteps: this.steps.length,
      isActive: true,
      showCloseButton: true,
      isRestart: true,
    });
  }

  nextStep(): void {
    if (this.currentStepIndex < this.steps.length - 1) {
      const nextIndex = this.currentStepIndex + 1;
      this.setPartial({
        currentStepIndex: nextIndex,
        currentStep: this.steps[nextIndex],
      });
      return;
    }

    void this.completeFeatureTour();
  }

  previousStep(): void {
    if (this.currentStepIndex <= 0) {
      return;
    }

    const previousIndex = this.currentStepIndex - 1;
    this.setPartial({
      currentStepIndex: previousIndex,
      currentStep: this.steps[previousIndex],
    });
  }

  async completeFeatureTour(): Promise<void> {
    const featureKey = this.currentFeature;
    if (!featureKey) {
      this.stopTour(false);
      return;
    }

    const index = this.pendingFeatureKeys.indexOf(featureKey);
    if (index >= 0) {
      this.pendingFeatureKeys.splice(index, 1);
    }

    if (this.pendingFeatureKeys.length > 0) {
      this.stopTour(false);
      await this.startNextQueuedTour();
      return;
    }

    this.stopTour(false);
  }

  skipFeatureTour(): void {
    this.pendingFeatureKeys = [];
    this.stopTour(true);
  }

  isRestartMode(): boolean {
    return this.isRestart;
  }

  async updateFeatureCompletion(featureKey: string): Promise<void> {
    try {
      const screenType = (sessionStorage.getItem('SCREEN.TYPE') ?? 'Standalone').trim() || 'Standalone';
      const country = (sessionStorage.getItem('ctry') ?? 'US').trim() || 'US';
      await api.fetch('/ActivityEvent/UpsertFeatureCompletion', {
        method: 'POST',
        body: JSON.stringify({
          context: this.getMetadataContext(DEFAULT_LANGUAGE),
          DocType: 'TourGuide',
          FeatureKey: featureKey,
          IsCompleted: true,
          LogType: 'internal',
          ScreenType: screenType,
          UserCountry: country,
          FeatureVersion: this.featureVersion,
        }),
      });
    } catch {
      // Fire-and-forget: completion tracking failure must not break the UI
    }
  }

  stopTour(clearState = true): void {
    // Invalidate any in-flight async startFeatureTour calls so that a
    // validateUser result that resolves after stopTour has been called cannot
    // accidentally re-activate the tour.
    ++this.tourStartGeneration;

    this.steps = [];
    this.setPartial({
      isActive: false,
      currentStep: null,
      currentStepIndex: 0,
      totalSteps: 0,
      showCloseButton: false,
      currentFeature: clearState ? null : this.currentFeature,
      isRestart: clearState ? false : this.isRestart,
    });

    if (clearState) {
      this.currentFeature = null;
      this.isRestart = false;
    }
  }
}

export const onboardingTourService = new OnboardingTourService();

export default onboardingTourService;
